
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavLink, NgbNavOutlet } from "@ng-bootstrap/ng-bootstrap";
import { FeatureService } from "company/components/features/feature.service";
import { Subscription } from "dexie";
import { CheckboxInputComponent } from "rl-common/components/checkbox-input/checkbox-input.component";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableMessageDirective } from "rl-common/components/form-table/form-table-message.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { NumberInputComponent } from "rl-common/components/number-input/number-input.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { CharDataType, ConstUtils, SystemIndicators } from "rl-common/rl-common.consts";
import { IPickListSource, ISourceField } from "rl-common/services/entity-config/entity-config.models";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { GrowlerService } from "rl-common/services/growler.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { SessionService } from "rl-common/services/session.service";
import { CharDataTypeUtil } from "rl-common/utils/char-data-type.util";
import { CharTypeIdUtil } from "rl-common/utils/char-type-id.util";
import { distinctUntilChanged, take, tap } from "rxjs/operators";
import { SourceFieldUniquenessValidationPanelComponent } from "./source-field-uniqueness-validation-panel/source-field-uniqueness-validation-panel.component";
import { SourceFieldUsedInPanelComponent } from "./source-field-used-in-panel/source-field-used-in-panel.component";

interface SourceFieldEditForm {
	charTypeId: FormControl<number>;
	sourceFieldId: FormControl<number>;
	sourceFieldName: FormControl<string>;
	mergeTag: FormControl<string>;
	dataType: FormControl<KeyValueOption>;
	length: FormControl<number>;
	systemIndicator: FormControl<number>;
	multipleIndicator: FormControl<boolean>;
	extractable: FormControl<boolean>;
	reportIndicator: FormControl<number>;
	enforceUniqueness: FormControl<boolean>;
	charValueSet: FormControl<KeyValueOption>;
	description: FormControl<string>;
	hint: FormControl<string>;
}

interface KeyValueOption {
	key: number | string;
	value: string;
}

@Component({
	selector: "rl-source-field-edit",
	templateUrl: "./source-field-edit.component.html",
	styleUrls: ["./source-field-edit.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FormsModule, TextInputComponent, DropdownSingleComponent, CheckboxInputComponent, NumberInputComponent, FormTableRowComponent, FormTableControlDirective, FormTableMessageDirective, SourceFieldUsedInPanelComponent, NgbNav, NgbNavOutlet, NgbNavItem, NgbNavLink, NgbNavContent, SourceFieldUniquenessValidationPanelComponent]
})
export class SourceFieldEditComponent implements OnInit, OnDestroy {
	private _entityConfigService = inject(EntityConfigService);
	private readonly _fb = inject(FormBuilder);
	private readonly _growlerService = inject(GrowlerService);
	private readonly _sessionService = inject(SessionService);
	private readonly _featureService = inject(FeatureService);
	private readonly _promptService = inject(PromptService);

	@Input()
	sourceField: ISourceField = null;

	@Input()
	charTypeId: number;

	@Input()
	hasCharValueSourceId: boolean;

	@Output()
	onComplete = new EventEmitter<boolean>();

	formLoaded = false;
	title = "";
	form: FormGroup<SourceFieldEditForm>;
	isSaving = false;
	systemSourceField = false;
	_subs: Subscription[] = [];

	dataTypeList: DropdownOptions<KeyValueOption> = {
		items: [],
		rowKeyFn: (type: KeyValueOption) => type.key,
		rowLabelFn: (type: KeyValueOption) => type.value
	};

	pickListSources: IPickListSource[];
	pickListSourceList: DropdownOptions<KeyValueOption> = {
		items: [],
		rowKeyFn: (type: KeyValueOption) => type.key,
		rowLabelFn: (type: KeyValueOption) => type.value
	};
	shouldAllowUniqueEnforcement = true;

	picklistDataTypeIds = [CharDataType.Money, CharDataType.Alphanumeric, CharDataType.Checkbox, CharDataType.Period];
	enforceUniqueAllowedDataTypes = [CharDataType.Alphanumeric, CharDataType.Number, CharDataType.Identifier];
	displayTabs: boolean = false;


	get isCreate() {
		return !this.sourceField;
	}

	get isEdit() {
		return !!this.sourceField;
	}

	get showMultiIndicator() {
		const charDataType = this.form?.controls?.dataType?.value?.key as CharDataType;
		return ![CharDataType.DateRange, CharDataType.NumberRange].includes(charDataType);
	}

	isSuperAdmin: boolean;

	ngOnInit(): void {
		const sub2 = this._sessionService.isRlAdmin$.pipe(
			take(1),
			tap(result => {
				this.isSuperAdmin = result;
			})
		).subscribe();
		this._subs.push(sub2);

		this.dataTypeList.items = ConstUtils.charDataTypeValues
			.filter(dt => this._featureService.isCharDataTypeEnabled(dt))
			.map(dt => {
				return { key: dt, value: CharDataTypeUtil.toDisplayName(dt) } as KeyValueOption;
			});

		this.setupPickListSourceList();
		if (this.sourceField === null) {
			// create mode
			this.title = "Create New Source Field";
			this.setupCreateSourceField();
		} else {
			this.title = "Edit " + CharTypeIdUtil.toReportLabel(this.sourceField.charTypeID) + " Source Field - <strong>" +
				this.sourceField.sourceFieldName + "</strong>";
			this.setupEditSourceField();
		}

		const sub = this.form?.controls?.dataType?.valueChanges.pipe(
			distinctUntilChanged()
		).subscribe(result => {
			const dataTypeId: CharDataType = result?.key as number;
			// If it's a picklist data type, then reset the char value set
			if (!this.picklistDataTypeIds.includes(dataTypeId)) {
				this.form.controls.charValueSet.reset();
			}

			// reset the multi indicator to its default
			const defaultMultiIndicator = ConstUtils.defaultMultiIndicator(dataTypeId);
			this.form.controls.multipleIndicator.setValue(!!defaultMultiIndicator);

			// enable/disable the multi indicator depending on what data type was selected
			const enableMultiple = ConstUtils.dataTypeSupportsMultiple(dataTypeId);
			if (enableMultiple) {
				this.form.controls.multipleIndicator.enable();
			} else {
				this.form.controls.multipleIndicator.disable();
			}
		});

		this.toggleBetweenMultAndUnique();
		this.toggleBetweenDataTypeAndUnique();

		this._subs.push(sub, sub2);
	}

	private toggleBetweenDataTypeAndUnique() {
		const sub = this.form.controls.dataType.valueChanges.pipe(distinctUntilChanged())
			.subscribe(dt => {
				const dataType = dt.key as CharDataType;
				if (!this.enforceUniqueAllowedDataTypes.includes(dataType)) {
					this.form.controls.enforceUniqueness.setValue(false);
					this.form.controls.enforceUniqueness.disable();
				} else if (dataType === CharDataType.Identifier) {
					this.form.controls.enforceUniqueness.setValue(true);
					this.form.controls.enforceUniqueness.disable();
				} else {
					this.form.controls.enforceUniqueness.enable();
				}
			});
		const sub2 = this.form.controls.charValueSet.valueChanges.pipe(distinctUntilChanged())
			.subscribe(charValueSet => {
				if (charValueSet?.key) {
					this.form.controls.enforceUniqueness.setValue(false);
					this.form.controls.enforceUniqueness.disable();
				} else {
					this.form.controls.enforceUniqueness.enable();
				}
			});
		this._subs.push(sub, sub2);
	}

	private toggleBetweenMultAndUnique() {
		const sub3 = this.form.controls.multipleIndicator.valueChanges.pipe(distinctUntilChanged())
			.subscribe(isMultiple => {
				if (isMultiple) {
					this.form.controls.enforceUniqueness.disable();
				} else {
					this.form.controls.enforceUniqueness.enable();
				}
			});
		const sub4 = this.form.controls.enforceUniqueness.valueChanges.pipe(distinctUntilChanged())
			.subscribe(enf => {
				if (enf) {
					this.form.controls.multipleIndicator.disable();
				} else {
					this.form.controls.multipleIndicator.enable();
				}
			});
		this._subs.push(sub3, sub4);
	}

	setupPickListSourceList() {
		const sub = this._entityConfigService.getPickListSources().pipe(
			tap(pickListSources => {
				this.pickListSources = [...pickListSources].sort((a, b) => (a.pickListSourceLabel.localeCompare(b.pickListSourceLabel)));
				for (const pls of this.pickListSources) {
					this.pickListSourceList.items.push({ key: pls.pickListSourceID, value: pls.pickListSourceLabel });
				}
			})
		).subscribe();

		this._subs.push(sub);
		this.pickListSourceList.items.push({ key: null, value: "Picklist Source" });
	}

	setupCreateSourceField() {
		const disableMultiple = this.sourceField?.dataTypeID ? !ConstUtils.dataTypeSupportsMultiple(this.sourceField?.dataTypeID) : false;
		const _fb = this._fb;
		this.form = _fb.group<SourceFieldEditForm>({
			charTypeId: _fb.control(this.charTypeId, Validators.required),
			sourceFieldId: _fb.control(null, {}), //Source Field ID generated on create
			sourceFieldName: _fb.control(null, Validators.required),
			mergeTag: _fb.control(null, Validators.required),
			description: _fb.control(null, {}),
			multipleIndicator: _fb.control({ value: null, disabled: disableMultiple }),
			extractable: _fb.control(true, {}),
			systemIndicator: _fb.control({ value: 0, disabled: !this.isSuperAdmin }),
			dataType: _fb.control({ key: CharDataType.Alphanumeric, value: "" }, Validators.required),
			length: _fb.control(10000, {}),
			charValueSet: _fb.control({ key: null, value: "" }, {}),
			reportIndicator: _fb.control(1, {}),
			enforceUniqueness: _fb.control(false, {}),
			hint: _fb.control(null, {})
		});

		const sub = this.form.controls.sourceFieldName.valueChanges.pipe(
			distinctUntilChanged()
		).subscribe(() => {
			const mergeTag = this.form.controls.sourceFieldName.value.trim()
				// Non-word and non-space characters
				.replace(/[^\w\s]/g, "")
				// Spaces with underscore
				.replace(/\s+/g, "_")
				.toLowerCase();
			this.form.controls.mergeTag.setValue(mergeTag);
		});
		this._subs.push(sub);

		this.subEnableDisableSystemIndicatorFields();

		this.formLoaded = true;
	}

	setupEditSourceField() {
		const disableMultiple = !ConstUtils.dataTypeSupportsMultiple(this.sourceField?.dataTypeID);
		const _fb = this._fb;
		this.form = _fb.group<SourceFieldEditForm>({
			charTypeId: _fb.control(this.sourceField.charTypeID, Validators.required),
			sourceFieldId: _fb.control(this.sourceField.sourceFieldID, Validators.required),
			sourceFieldName: _fb.control(this.sourceField.sourceFieldName, Validators.required),
			mergeTag: _fb.control(this.sourceField.mergeTag, Validators.required),
			description: _fb.control(this.sourceField.description, {}),
			multipleIndicator: _fb.control({ value: !!this.sourceField.multipleIndicator, disabled: disableMultiple }),
			extractable: _fb.control(this.sourceField.extractable, {}),
			systemIndicator: _fb.control({ value: this.sourceField.systemIndicator, disabled: !this.isSuperAdmin }),
			dataType: _fb.control({ value: { key: this.sourceField.dataTypeID, value: "" }, disabled: true }, Validators.required),
			length: _fb.control(this.sourceField.length, {}),
			charValueSet: _fb.control({ value: { key: this.sourceField.charValueSetID, value: "" }, disabled: this.hasCharValueSourceId }),
			reportIndicator: _fb.control(this.sourceField.reportIndicator, {}),
			enforceUniqueness: _fb.control(this.sourceField.enforceUnique, {}),
			hint: _fb.control(this.sourceField.hint, {})
		});

		this.subEnableDisableSystemIndicatorFields();

		if (this.sourceField.systemIndicator !== 0) {
			this.form.controls.mergeTag.disable();
			this.form.controls.multipleIndicator.disable();
			this.form.controls.extractable.disable();
			this.form.controls.dataType.disable();
			this.form.controls.length.disable();
		}

		const enforceUniqueAllowedDataTypes = [CharDataType.Alphanumeric, CharDataType.Number, CharDataType.Identifier];
		const hasPicklist = this.sourceField.charValueSetID;
		if (!enforceUniqueAllowedDataTypes.includes(this.sourceField.dataTypeID) || hasPicklist) {
			this.form.controls.enforceUniqueness.disable();
			this.shouldAllowUniqueEnforcement = false;
		}

		if (this.sourceField.enforceUnique) {
			this.form.controls.multipleIndicator.disable();
		}

		const sub = this._entityConfigService.getCharacteristicDataCount(this.charTypeId, this.sourceField.sourceFieldID)
			.subscribe(charDataCount => {
				if (charDataCount === 0) {
					this.form.controls.dataType.enable();
				}
			});

		this.systemSourceField = (this.sourceField.systemIndicator === SystemIndicators.System);
		this.displayTabs = this.form.controls.enforceUniqueness?.value && this.isSuperAdmin;

		this.formLoaded = true;
		this._subs.push(sub);
	}

	private subEnableDisableSystemIndicatorFields() {
		const sub = this.form.controls.systemIndicator.valueChanges.subscribe(value => {
			if (value !== 0) {
				this.form.controls.mergeTag.disable();
				this.form.controls.multipleIndicator.disable();
				this.form.controls.extractable.disable();
				this.form.controls.dataType.disable();
				this.form.controls.length.disable();
			} else {
				this.form.controls.mergeTag.enable();
				this.form.controls.multipleIndicator.enable();
				this.form.controls.extractable.enable();
				this.form.controls.dataType.enable();
				this.form.controls.length.enable();
			}
			this.systemSourceField = (value === SystemIndicators.System);
		});
		this._subs.push(sub);
	}

	async submit($event: Event) {
		if (this.form.invalid) {
			$event.preventDefault();
		} else {
			this.isSaving = true;
			let success = true;
			let errorMessage = "";
			try {
				const sourceField: Partial<ISourceField> = {
					charTypeID: this.form.controls.charTypeId.value,
					sourceFieldID: this.form.controls.sourceFieldId.value,
					sourceFieldName: this.form.controls.sourceFieldName.value,
					mergeTag: this.form.controls.mergeTag.value,
					dataTypeID: this.form.controls.dataType.value.key as number,
					length: this.form.controls.length.value,
					systemIndicator: this.form.controls.systemIndicator.value,
					multipleIndicator: +this.form.controls.multipleIndicator.value,
					extractable: this.form.controls.extractable.value,
					reportIndicator: +this.form.controls.reportIndicator.value,
					charValueSetID: this.form.controls.charValueSet.value?.key as string,
					description: this.form.controls.description.value,
					hint: this.form.controls.hint.value,
					enforceUnique: this.form.controls.enforceUniqueness.value
				};

				if (this.isCreate) {
					await this._entityConfigService.createSourceField(sourceField as ISourceField).toPromise();
				}
				if (this.isEdit) {
					await this._entityConfigService.updateSourceField(sourceField as ISourceField).toPromise();
				}
			} catch (e) {
				success = false;
				errorMessage = e?.error?.message;
			} finally {
				this.isSaving = false;
			}
			if (success) {
				this.onComplete.next(true);
				this._growlerService.success().growl("Source Field saved.");
			} else {
				this._growlerService.error().growl(errorMessage);
			}
		}
	}

	getNumericValue(value: number | string): number {
		return value as number;
	}

	close() {
		this.onComplete.next(null);
	}

	toggleSidePanel(cancelPrompt: TemplateRef<unknown>, isChecked: boolean) {
		const confirmTitle = "Are you sure?";
		const sub = this._promptService.confirmMini(confirmTitle, cancelPrompt)
			.subscribe(confirm => {
				if (!confirm) {
					this.form.controls.enforceUniqueness.setValue(!isChecked);
				}
				this.displayTabs = this.form.controls.enforceUniqueness.value;
			})
		this._subs.push(sub);
	}

	ngOnDestroy(): void {
		this._subs.forEach(sub => sub?.unsubscribe());
	}
}
